import { useState } from 'react';
import { Clock, Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { Contraction } from '../types';
import { formatTimeOfDay, formatDurationReadable, formatIntervalReadable } from '../utils/formatTime';

interface ContractionHistoryProps {
  contractions: Contraction[];
  onDelete: (id: string) => Promise<boolean>;
  onDeleteAll: () => Promise<boolean>;
}

type TabType = 'recent' | 'all';

export function ContractionHistory({
  contractions,
  onDelete,
  onDeleteAll,
}: ContractionHistoryProps) {
  const { t } = useTranslation('contractionTimer');
  const [activeTab, setActiveTab] = useState<TabType>('recent');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filter completed contractions and sort by start time (newest first)
  const completedContractions = contractions
    .filter((c) => c.end_time !== null)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  // Get contractions to display based on active tab
  const displayedContractions =
    activeTab === 'recent' ? completedContractions.slice(0, 5) : completedContractions;

  const totalCount = completedContractions.length;

  const handleDeleteOne = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      await onDeleteAll();
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white/10 rounded-[15px] overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-white/20">
        <button
          onClick={() => setActiveTab('recent')}
          className={cn(
            'flex-1 py-3 px-4 text-sm font-medium transition-colors',
            activeTab === 'recent'
              ? 'text-pp-orange border-b-2 border-pp-orange'
              : 'text-white/70 hover:text-white'
          )}
        >
          {t('history.title')}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={cn(
            'flex-1 py-3 px-4 text-sm font-medium transition-colors',
            activeTab === 'all'
              ? 'text-pp-orange border-b-2 border-pp-orange'
              : 'text-white/70 hover:text-white'
          )}
        >
          {t('history.viewAll', { count: totalCount })}
        </button>
      </div>

      {/* Content */}
      <div className="divide-y divide-white/10">
        {displayedContractions.length === 0 ? (
          <div className="py-8 text-center">
            <Clock className="w-10 h-10 mx-auto text-white/30 mb-2" />
            <p className="text-white/70 text-sm">
              {t('history.empty')}
            </p>
            <p className="text-white/50 text-xs mt-1">
              {t('history.emptyHint')}
            </p>
          </div>
        ) : (
          <>
            {displayedContractions.map((contraction, index) => {
              const reversedIndex = totalCount - index;
              return (
                <div
                  key={contraction.id}
                  className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                >
                  {/* Number Badge */}
                  <div className="w-8 h-8 rounded-full bg-pp-orange/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-pp-orange">
                      {reversedIndex}
                    </span>
                  </div>

                  {/* Time */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">
                        {formatTimeOfDay(contraction.start_time)}
                      </span>
                      {contraction.duration_seconds !== null &&
                        contraction.duration_seconds > 90 && (
                          <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                            {t('history.longContraction')}
                          </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/60 mt-0.5">
                      <span>
                        {t('history.duration')}{' '}
                        {contraction.duration_seconds !== null
                          ? formatDurationReadable(contraction.duration_seconds)
                          : '-'}
                      </span>
                      {contraction.interval_seconds !== null && (
                        <span>
                          {t('history.interval')} {formatIntervalReadable(contraction.interval_seconds)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Delete Button (only in all tab) */}
                  {activeTab === 'all' && (
                    <button
                      onClick={() => handleDeleteOne(contraction.id)}
                      disabled={deletingId !== null}
                      className="p-2 text-white/50 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                      aria-label={t('history.delete')}
                    >
                      {deletingId === contraction.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}

            {/* Delete All Button (only in all tab) */}
            {activeTab === 'all' && totalCount > 0 && (
              <div className="p-3">
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full py-2.5 text-red-400 text-sm font-medium rounded-xl border border-red-500/30 hover:bg-red-500/20 transition-colors"
                  >
                    {t('history.deleteAll')}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t('history.deleteAllConfirm', { count: totalCount })}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        className="flex-1 py-2 text-sm font-medium text-white/70 bg-white/10 rounded-xl hover:bg-white/20 transition-colors disabled:opacity-50"
                      >
                        {t('history.cancel')}
                      </button>
                      <button
                        onClick={handleDeleteAll}
                        disabled={isDeleting}
                        className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {isDeleting ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          t('history.delete')
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
